// Reads and the one write of the demand pool (spec 03 §5 rows 12 to 16), all
// through the generated client.

import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { components, paths } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { refreshQuery, shouldRetryRead, type ReadContext } from "../../../../lib/query";
import { newIdempotencyKey, unwrap } from "../../../../lib/request";

type S = components["schemas"];
export type DemandRefresh = S["DemandRefresh"];
export type DemandRefreshDetail = S["DemandRefreshDetail"];
export type DemandRefreshList = S["DemandRefreshListResponse"];
export type DemandPendingRequest = NonNullable<S["DemandPendingRequest"]>;
export type DemandEntry = S["DemandEntry"];
export type DemandEntries = S["DemandEntriesResponse"];
export type DemandSubtotal = S["DemandSubtotal"];
export type SalesOrderLabel = S["SalesOrderLabel"];
export type ForecastConsumption = S["ForecastConsumption"];
export type DemandSourceKind = DemandEntry["source_kind"];
export type DemandDateBasis = NonNullable<DemandEntry["date_basis"]>;
type EntriesQuery = NonNullable<paths["/v1/demand/entries"]["get"]["parameters"]["query"]>;

export const DEMAND_SOURCE_KINDS: readonly DemandSourceKind[] = [
  "sales_order",
  "forecast",
  "safety_stock",
];
export const DEMAND_DATE_BASES: readonly DemandDateBasis[] = [
  "line_promise",
  "undated",
  "past_due",
];

/** How often a screen re-reads while a refresh runs or a request waits. */
export const DEMAND_BUSY_POLL_MS = 5_000;
/** How often the workbench re-reads otherwise, so an event refresh shows up unasked. */
export const DEMAND_IDLE_POLL_MS = 60_000;

export interface DemandEntryFilters {
  readonly product_id?: string | undefined;
  readonly source_kind?: DemandSourceKind | undefined;
  readonly bucket_from?: string | undefined;
  readonly bucket_to?: string | undefined;
  readonly planner_code?: string | undefined;
  readonly date_basis?: DemandDateBasis | undefined;
}

export const demandKeys = {
  all: (facilityId: string) => ["planning", "demand", facilityId] as const,
  refreshes: (facilityId: string, cursor: string | undefined) =>
    ["planning", "demand", facilityId, "refreshes", cursor ?? null] as const,
  refresh: (id: string) => ["planning", "demand-refresh", id] as const,
  entries: (
    facilityId: string,
    refreshId: string | undefined,
    filters: DemandEntryFilters,
    cursor: string | undefined,
  ) =>
    [
      "planning",
      "demand",
      facilityId,
      "entries",
      refreshId ?? null,
      filters,
      cursor ?? null,
    ] as const,
  consumption: (forecastId: string, refreshId: string) =>
    ["planning", "demand-consumption", forecastId, refreshId] as const,
};

function busy(list: DemandRefreshList | undefined): boolean {
  if (list === undefined) return false;
  return list.pending_request !== null || list.items.some((r) => r.status === "running");
}

/** One page of the facility's refresh history, with the pending request and the failure count. */
export function useDemandRefreshes(facilityId: string, cursor: string | undefined) {
  return useQuery({
    queryKey: demandKeys.refreshes(facilityId, cursor),
    enabled: facilityId.length > 0,
    retry: shouldRetryRead,
    refetchInterval: (query) =>
      busy(query.state.data) ? DEMAND_BUSY_POLL_MS : DEMAND_IDLE_POLL_MS,
    queryFn: async ({ signal }: ReadContext): Promise<DemandRefreshList> =>
      unwrap(
        await client.GET("/v1/demand/refreshes", {
          signal,
          params: {
            query: {
              facility_id: facilityId,
              limit: 20,
              ...(cursor !== undefined ? { cursor } : {}),
            },
          },
        }),
        "demand refreshes",
      ),
  });
}

/** One refresh with the labels of its skipped and deferred lines; re-read while it runs. */
export function useDemandRefresh(id: string | undefined) {
  return useQuery({
    queryKey: demandKeys.refresh(id ?? ""),
    enabled: id !== undefined && id.length > 0,
    retry: shouldRetryRead,
    refetchInterval: (query) =>
      query.state.data?.status === "running" ? DEMAND_BUSY_POLL_MS : false,
    queryFn: async ({ signal }: ReadContext): Promise<DemandRefreshDetail> =>
      unwrap(
        await client.GET("/v1/demand/refreshes/{id}", {
          signal,
          params: { path: { id: id ?? "" } },
        }),
        "demand refresh",
      ),
  });
}

/**
 * One page of the pool. Without `refreshId` the server reads the facility's
 * latest succeeded refresh; the screen then pins the one it was answered so
 * every later page reads the same pool.
 */
export function useDemandEntries(
  facilityId: string,
  refreshId: string | undefined,
  filters: DemandEntryFilters,
  cursor: string | undefined,
  options: { limit?: number; enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: demandKeys.entries(facilityId, refreshId, filters, cursor),
    enabled: facilityId.length > 0 && options.enabled !== false,
    retry: shouldRetryRead,
    queryFn: async ({ signal }: ReadContext): Promise<DemandEntries> => {
      const query: EntriesQuery = { facility_id: facilityId, limit: options.limit ?? 50 };
      if (refreshId !== undefined) query.refresh_id = refreshId;
      if (cursor !== undefined) query.cursor = cursor;
      if (filters.product_id) query.product_id = filters.product_id;
      if (filters.source_kind) query.source_kind = filters.source_kind;
      if (filters.bucket_from) query.bucket_from = filters.bucket_from;
      if (filters.bucket_to) query.bucket_to = filters.bucket_to;
      if (filters.planner_code) query.planner_code = filters.planner_code;
      if (filters.date_basis) query.date_basis = filters.date_basis;
      return unwrap(
        await client.GET("/v1/demand/entries", { signal, params: { query } }),
        "demand pool",
      );
    },
  });
}

/** Which order lines consumed a forecast's cells in one refresh (§2.6b). */
export function useForecastConsumption(forecastId: string | undefined, refreshId: string) {
  return useQuery({
    queryKey: demandKeys.consumption(forecastId ?? "", refreshId),
    enabled: forecastId !== undefined && forecastId.length > 0,
    retry: shouldRetryRead,
    queryFn: async ({ signal }: ReadContext): Promise<ForecastConsumption> =>
      unwrap(
        await client.GET("/v1/forecasts/{id}/consumption", {
          signal,
          params: { path: { id: forecastId ?? "" }, query: { refresh_id: refreshId } },
        }),
        "forecast consumption",
      ),
  });
}

/** `POST /v1/demand/refresh`: the claimed run, or the one already running. */
export async function requestDemandRefresh(facilityId: string) {
  return unwrap(
    await client.POST("/v1/demand/refresh", {
      params: { header: { "Idempotency-Key": newIdempotencyKey() } },
      body: { facility_id: facilityId },
    }),
    "demand refresh",
  );
}

/** Re-reads every demand read of a facility, after a refresh finishes or starts. */
export function useDemandRefreshAll() {
  const qc = useQueryClient();
  return useCallback((facilityId: string) => refreshQuery(qc, demandKeys.all(facilityId)), [qc]);
}
